<script lang="ts">
  import { X } from 'lucide-svelte';
  import { createRoomProduct } from '$lib/api/room_product';
  import { productsQuery } from '$lib/queries/products';
  import { roomsQuery } from '$lib/queries/rooms';
  import { showError, showSuccess } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import type { ProductResponse } from '$lib/types/product';

  let {
    product = null,
    onsuccess = () => {},
    oncancel = () => {}
  }: {
    product?: ProductResponse | null;
    onsuccess?: () => void;
    oncancel?: () => void;
  } = $props();

  const products = productsQuery();
  const rooms = roomsQuery();

  // Categorise the picker: group products by category for an easy browse.
  const groupedProducts = $derived.by(() => {
    const byCat = new Map<string, ProductResponse[]>();
    for (const p of products.data ?? []) {
      const key = p.category_name || 'Uncategorised';
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key)!.push(p);
    }
    return [...byCat.entries()].map(([category, items]) => ({ category, items }));
  });

  let selectedProduct: ProductResponse | null = product;
  let selectedRoom: number | null = null;
  let selectedQuantity = 1;
  let addRoomNotes = '';

  async function addToRoom() {
    if (!selectedProduct || !selectedRoom) return;
    try {
      await createRoomProduct({
        room: selectedRoom,
        product: selectedProduct.id,
        quantity: selectedQuantity,
        notes: addRoomNotes
      });
      showSuccess('Product added to room');
      onsuccess();
    } catch (error) {
      showError(getErrorMessage(error));
    }
  }
</script>

<form>
  {#if !product}
    <div class="mb-4">
      <label for="product" class="block mb-1 text-sm font-medium text-text-secondary"
        >Product Name</label
      >
      <select
        id="product"
        bind:value={selectedProduct}
        class="w-full px-3 py-2 border border-border-strong rounded-lg"
      >
        {#each groupedProducts as group}
          <optgroup label={group.category}>
            {#each group.items as p}
              <option value={p}>{p.name}{p.type_name ? ` · ${p.type_name}` : ''}</option>
            {/each}
          </optgroup>
        {/each}
      </select>
    </div>
  {/if}
  <div class="mb-4">
    <label for="room" class="block mb-1 text-sm font-medium text-text-secondary">Room Name</label>
    <select
      id="room"
      bind:value={selectedRoom}
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
    >
      {#each rooms.data ?? [] as room}
        <option value={room.id}>{room.name}</option>
      {/each}
    </select>
  </div>
  <div class="mb-4">
    <label for="quantity" class="block mb-1 text-sm font-medium text-text-secondary">Quantity</label
    >
    <input
      type="number"
      bind:value={selectedQuantity}
      id="quantity"
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
      placeholder="Quantity"
    />
  </div>
  <div class="mb-4">
    <label for="notes" class="block mb-1 text-sm font-medium text-text-secondary">Notes</label>
    <textarea
      id="notes"
      bind:value={addRoomNotes}
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
      placeholder="Notes"
    ></textarea>
  </div>
  <div class="flex justify-end gap-2">
    <button
      type="button"
      onclick={oncancel}
      class="px-4 py-2 bg-bg-hover hover:bg-bg-hover text-red-600 rounded-lg flex"
    >
      <X class="w-6 h-6" />
      Cancel
    </button>
    <button
      type="button"
      onclick={addToRoom}
      class="px-4 py-2 bg-accent-primary hover:opacity-90 text-white rounded-lg"
    >
      Add
    </button>
  </div>
</form>
